import { useMemo, useState } from 'react';
import {
  BODY_FONT_OPTIONS,
  DISPLAY_FONT_OPTIONS,
  FONT_BASE_REM_DEFAULT,
  FONT_BASE_REM_MAX,
  FONT_BASE_REM_MIN,
  PX_PER_REM,
  getBodyFontOption,
  getBrandingSuggestion,
  getColorThemeOption,
  getDisplayFontOption,
  sanitizeFontBaseRem,
} from '@palacete/shared/branding';
import { api } from '../../lib/api';
import AsyncActionButton from '../../components/ui/AsyncActionButton';
import { MediaPicker } from '../../components/ui/MediaPicker';
import { panelMeta } from '../../lib/panelMeta';
import type {
  BackgroundStyle, BodyFont, CardBorderColor, CardBorderWidth, CardStyle, ColorTheme, CornerRadius,
  Density, DisplayFont, GlassBlur, SurfaceStyle, ThemeData,
} from './sitePageTypes';

interface Props {
  industry: string | null;
  theme: ThemeData;
  navLogoUrl: string;
  footerLogoUrl: string;
  footerLogoMobileUrl: string;
  navLogoShowName: boolean;
  footerLogoShowName: boolean;
  faviconUrl: string;
  saving: boolean;
  setTheme: (theme: ThemeData) => void;
  setNavLogoUrl: (value: string) => void;
  setFooterLogoUrl: (value: string) => void;
  setFooterLogoMobileUrl: (value: string) => void;
  setNavLogoShowName: (value: boolean) => void;
  setFooterLogoShowName: (value: boolean) => void;
  setFaviconUrl: (value: string) => void;
  onSave: () => Promise<void>;
}

export default function SiteAppearanceTab(props: Props) {
  const {
    industry,
    theme,
    navLogoUrl,
    footerLogoUrl,
    footerLogoMobileUrl,
    navLogoShowName,
    footerLogoShowName,
    faviconUrl,
    saving,
    setTheme,
    setNavLogoUrl,
    setFooterLogoUrl,
    setFooterLogoMobileUrl,
    setNavLogoShowName,
    setFooterLogoShowName,
    setFaviconUrl,
    onSave,
  } = props;
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const suggestion = useMemo(() => getBrandingSuggestion(industry), [industry]);
  const canApplyBaseSuggestion = theme.brandingSource !== 'ai-refined';
  const activePalette = getColorThemeOption(theme.colorTheme ?? suggestion.theme.colorTheme);
  const activeDisplayFont = getDisplayFontOption(theme.fontDisplay ?? suggestion.theme.fontDisplay);
  const activeBodyFont = getBodyFontOption(theme.fontBody ?? suggestion.theme.fontBody);
  const displayFontGroups = useMemo(() => groupOptionsByCategory(DISPLAY_FONT_OPTIONS), []);
  const bodyFontGroups = useMemo(() => groupOptionsByCategory(BODY_FONT_OPTIONS), []);

  async function handleAiBranding() {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    setAiError(null);
    setAiNote(null);
    try {
      const result = await api.ai.branding({
        instruction: aiPrompt.trim(),
        industry,
        currentTheme: theme as Record<string, unknown>,
      });
      setTheme(result.theme as ThemeData);
      setAiNote(result.explanation);
    } catch (error) {
      setAiError((error as Error).message);
    } finally {
      setAiBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-ui-text">Branding sugerido para tu giro</h2>
          <p className="text-sm text-ui-text-muted">
            Te propusimos esta paleta y estas fuentes para arrancar con una base coherente.
            Después puedes ajustarlas manualmente o pedirle a la IA una nueva versión.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="rounded-xl border border-ui-border bg-ui-bg p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-ui-text">{suggestion.label}</p>
                <p className="text-sm text-ui-text-subtle">{suggestion.paletteName}</p>
              </div>
              {canApplyBaseSuggestion && (
                <button
                  type="button"
                  onClick={() => {
                    setTheme(suggestion.theme);
                    setAiNote('Aplicamos la sugerencia base para tu industria. Revisa el resultado y guarda si te convence.');
                    setAiError(null);
                  }}
                  className="rounded-md border border-ui-border px-3 py-1.5 text-sm text-ui-text hover:border-ui-accent hover:text-ui-accent transition-colors"
                >
                  Aplicar sugerencia
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <span className="h-10 flex-1 rounded-lg border border-ui-border" style={{ backgroundColor: theme.accent ?? suggestion.theme.accent }} />
              <span className="h-10 flex-1 rounded-lg border border-ui-border" style={{ backgroundColor: theme.deepAlt ?? suggestion.theme.deepAlt }} />
              <span className="h-10 flex-1 rounded-lg border border-ui-border bg-ui-surface" />
            </div>

            <p className="text-sm text-ui-text-subtle">{suggestion.rationale}</p>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="rounded-lg border border-ui-border bg-ui-surface px-3 py-2">
                <p className="text-sm text-ui-text-subtle">Títulos</p>
                <p className="text-sm font-medium text-ui-text">{activeDisplayFont?.label ?? 'Sin definir'}</p>
                <p className="text-sm text-ui-text-subtle">{activeDisplayFont?.provider ?? 'Catálogo soportado'}</p>
              </div>
              <div className="rounded-lg border border-ui-border bg-ui-surface px-3 py-2">
                <p className="text-sm text-ui-text-subtle">Texto</p>
                <p className="text-sm font-medium text-ui-text">{activeBodyFont?.label ?? 'Sin definir'}</p>
                <p className="text-sm text-ui-text-subtle">{activeBodyFont?.provider ?? 'Catálogo soportado'}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-ui-border bg-ui-bg p-4 space-y-3">
            <div>
              <label className="block text-sm text-ui-text-muted mb-1.5">
                Describe el estilo que quieres
              </label>
              <textarea
                value={aiPrompt}
                onChange={(event) => setAiPrompt(event.target.value)}
                rows={4}
                placeholder="Ej. Hazlo más sobrio y premium, con menos contraste agresivo y un tono más editorial."
                className="w-full resize-none rounded-lg border border-ui-border bg-ui-surface px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>

            <p className="text-sm text-ui-text-subtle">
              La IA ajusta colores y tipografías dentro de un catálogo curado y compatible con tu sitio.
            </p>

            {aiError && <p className="text-sm text-ui-danger">{aiError}</p>}
            {aiNote && <p className="text-sm text-ui-text-muted">{aiNote}</p>}

            <button
              type="button"
              onClick={handleAiBranding}
              disabled={aiBusy || !aiPrompt.trim()}
              className="rounded-md bg-ui-accent px-4 py-2 text-sm text-ui-text-inverse disabled:opacity-50 hover:opacity-90"
            >
              {aiBusy ? 'Ajustando…' : 'Ajustar con IA'}
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LogoPicker
          label="Logo navegación"
          value={navLogoUrl}
          showName={navLogoShowName}
          onChange={(value) => {
            setNavLogoUrl(value);
            if (value) setNavLogoShowName(false);
          }}
          onToggleShowName={setNavLogoShowName}
        />
        <LogoPicker
          label="Logo footer"
          value={footerLogoUrl}
          showName={footerLogoShowName}
          onChange={(value) => {
            setFooterLogoUrl(value);
            if (value) setFooterLogoShowName(false);
          }}
          onToggleShowName={setFooterLogoShowName}
        />
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">
            Logo footer móvil <span className="text-ui-text-subtle">(vertical)</span>
          </label>
          <MediaPicker
            value={footerLogoMobileUrl ? [footerLogoMobileUrl] : []}
            onChange={(urls) => setFooterLogoMobileUrl(urls[0] ?? '')}
            multiple={false}
            folder="settings"
            variant="large"
          />
          <p className="text-sm text-ui-text-subtle mt-2">Opcional — se muestra en móvil en lugar del logo footer</p>
        </div>
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Favicon</label>
          <div className="max-w-[120px]">
            <MediaPicker
              value={faviconUrl ? [faviconUrl] : []}
              onChange={(urls) => setFaviconUrl(urls[0] ?? '')}
              multiple={false}
              folder="settings"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <SelectField
          label="Paleta de colores"
          value={theme.colorTheme ?? ''}
          onChange={(value) => setTheme({ ...theme, colorTheme: (value as ColorTheme) || undefined })}
        >
          <option value="">— por defecto —</option>
          <optgroup label={panelMeta.collectionPaletteLabel}>
            <option value="crema">Crema</option>
            <option value="lino">Lino</option>
            <option value="carbon">Carbón</option>
            <option value="tinta">Tinta</option>
          </optgroup>
          <optgroup label="Genéricos">
            <option value="slate">Slate</option>
            <option value="ocean">Ocean</option>
            <option value="forest">Forest</option>
            <option value="stone">Stone</option>
          </optgroup>
        </SelectField>

        <SelectField
          label="Fuente títulos"
          value={theme.fontDisplay ?? ''}
          onChange={(value) => setTheme({ ...theme, fontDisplay: (value as DisplayFont) || undefined })}
        >
          <option value="">— por defecto —</option>
          {displayFontGroups.map(([category, options]) => (
            <optgroup key={category} label={category}>
              {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </optgroup>
          ))}
        </SelectField>

        <SelectField
          label="Fuente cuerpo"
          value={theme.fontBody ?? ''}
          onChange={(value) => setTheme({ ...theme, fontBody: (value as BodyFont) || undefined })}
        >
          <option value="">— por defecto —</option>
          {bodyFontGroups.map(([category, options]) => (
            <optgroup key={category} label={category}>
              {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </optgroup>
          ))}
        </SelectField>

        <SelectField
          label="Densidad"
          value={theme.density ?? ''}
          onChange={(value) => setTheme({ ...theme, density: (value as Density) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="compact">Compacto</option>
          <option value="regular">Regular</option>
          <option value="spacious">Espacioso</option>
        </SelectField>

        <PxField
          label="Tamaño de fuente base"
          remValue={theme.fontBaseRem}
          onChangeRem={(rem) => setTheme({ ...theme, fontBaseRem: rem })}
        />

        <SelectField
          label="Estilo de tarjetas"
          value={theme.cardStyle ?? ''}
          onChange={(value) => setTheme({ ...theme, cardStyle: (value as CardStyle) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="shadow">Sombra</option>
          <option value="frame">Marco</option>
          <option value="minimal">Minimal</option>
        </SelectField>

        <SelectField
          label="Bordes"
          value={theme.cornerRadius ?? ''}
          onChange={(value) => setTheme({ ...theme, cornerRadius: (value as CornerRadius) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="none">Sin redondeo</option>
          <option value="subtle">Sutil</option>
          <option value="rounded">Redondeado</option>
        </SelectField>

        <SelectField
          label="Color borde tarjeta"
          value={theme.cardBorderColor ?? ''}
          onChange={(value) => setTheme({ ...theme, cardBorderColor: (value as CardBorderColor) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="gold">Dorado</option>
          <option value="accent">Acento</option>
          <option value="ink">Tinta</option>
          <option value="soft">Suave</option>
        </SelectField>

        <SelectField
          label="Grosor borde tarjeta"
          value={theme.cardBorderWidth ?? ''}
          onChange={(value) => setTheme({ ...theme, cardBorderWidth: (value as CardBorderWidth) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="thin">Fino</option>
          <option value="regular">Regular</option>
          <option value="thick">Grueso</option>
        </SelectField>

        <SelectField
          label="Acabado de superficies"
          value={theme.surfaceStyle ?? ''}
          onChange={(value) => setTheme({ ...theme, surfaceStyle: (value as SurfaceStyle) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="solid">Sólido</option>
          <option value="glass">Cristal (glass)</option>
        </SelectField>

        <SelectField
          label="Fondo de página"
          value={theme.backgroundStyle ?? ''}
          onChange={(value) => setTheme({ ...theme, backgroundStyle: (value as BackgroundStyle) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="plain">Plano</option>
          <option value="mesh">Degradado mesh</option>
        </SelectField>

        <SelectField
          label="Intensidad del cristal"
          value={theme.glassBlur ?? ''}
          onChange={(value) => setTheme({ ...theme, glassBlur: (value as GlassBlur) || undefined })}
        >
          <option value="">— por defecto —</option>
          <option value="subtle">Sutil</option>
          <option value="regular">Regular</option>
          <option value="strong">Fuerte</option>
        </SelectField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ColorInput
          label="Color acento (hex)"
          value={theme.accent ?? ''}
          placeholder="#a07850"
          fallback="#000000"
          onChange={(value) => setTheme({ ...theme, accent: value || undefined })}
        />
        <ColorInput
          label="Color sección oscura alt (hex)"
          value={theme.deepAlt ?? ''}
          placeholder="#1a1a2e"
          fallback="#000000"
          onChange={(value) => setTheme({ ...theme, deepAlt: value || undefined })}
        />
        <ColorInput
          label="Color secundario / brillo del mesh (hex)"
          value={theme.accent2 ?? ''}
          placeholder="#e0a33e"
          fallback="#e0a33e"
          onChange={(value) => setTheme({ ...theme, accent2: value || undefined })}
        />
        <ColorInput
          label="Tercer color de marca / highlight (hex)"
          value={theme.accent3 ?? ''}
          placeholder="#ffd700"
          fallback="#ffd700"
          onChange={(value) => setTheme({ ...theme, accent3: value || undefined })}
        />
      </div>

      <div className="rounded-xl border border-ui-border bg-ui-surface px-4 py-3">
        <p className="text-sm text-ui-text">
          La familia visual activa usa <span className="font-medium">{activePalette?.label ?? 'la paleta actual'}</span>,
          {' '}títulos en <span className="font-medium">{activeDisplayFont?.label ?? 'la fuente actual'}</span>
          {' '}y texto en <span className="font-medium">{activeBodyFont?.label ?? 'la fuente actual'}</span>.
        </p>
        <p className="text-sm text-ui-text-subtle mt-1">
          Todas las tipografías del catálogo están soportadas por el renderer y se cargan desde Google Fonts.
        </p>
      </div>

      <AsyncActionButton
        idleLabel="Guardar apariencia"
        loadingLabel="Guardando apariencia…"
        successLabel="Cambios guardados. Publica tu sitio para que se vean."
        errorLabel="No se pudo guardar la apariencia"
        disabled={saving}
        onAction={onSave}
        className="bg-ui-accent text-ui-text-inverse"
      />
    </div>
  );
}

function LogoPicker({
  label,
  value,
  showName,
  onChange,
  onToggleShowName,
}: {
  label: string;
  value: string;
  showName: boolean;
  onChange: (value: string) => void;
  onToggleShowName: (value: boolean) => void;
}) {
  return (
    <div>
      <label className="block text-sm text-ui-text-muted mb-1">{label}</label>
      <MediaPicker
        value={value ? [value] : []}
        onChange={(urls) => onChange(urls[0] ?? '')}
        multiple={false}
        folder="settings"
        variant="large"
      />
      <label className="flex items-center gap-2 mt-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showName}
          onChange={(event) => onToggleShowName(event.target.checked)}
          className="rounded border-ui-border"
        />
        <span className="text-sm text-ui-text-muted">Mostrar nombre</span>
      </label>
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm text-ui-text-muted mb-1">{label}</label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
      >
        {children}
      </select>
    </div>
  );
}

/** Campo de tamaño en píxeles: el usuario edita px, pero el dato se persiste en rem
 *  (px = rem × 16). Vacío = por defecto (lo gobierna el enum heredado fontScale). */
function PxField({
  label,
  remValue,
  onChangeRem,
}: {
  label: string;
  remValue: number | undefined;
  onChangeRem: (rem: number | undefined) => void;
}) {
  const pxValue = typeof remValue === 'number' ? Math.round(remValue * PX_PER_REM) : '';
  const minPx = Math.round(FONT_BASE_REM_MIN * PX_PER_REM);
  const maxPx = Math.round(FONT_BASE_REM_MAX * PX_PER_REM);
  const defaultPx = Math.round(FONT_BASE_REM_DEFAULT * PX_PER_REM);
  return (
    <div>
      <label className="block text-sm text-ui-text-muted mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={minPx}
          max={maxPx}
          step={1}
          value={pxValue}
          placeholder={`${defaultPx}`}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === '') { onChangeRem(undefined); return; }
            onChangeRem(sanitizeFontBaseRem(Number(raw) / PX_PER_REM));
          }}
          className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
        />
        <span className="text-sm text-ui-text-subtle">px</span>
      </div>
    </div>
  );
}

function groupOptionsByCategory<T extends string>(options: Array<{ value: T; label: string; category?: string }>) {
  const groups = new Map<string, Array<{ value: T; label: string }>>();
  for (const option of options) {
    const category = option.category ?? 'Curadas';
    const current = groups.get(category) ?? [];
    current.push({ value: option.value, label: option.label });
    groups.set(category, current);
  }
  return Array.from(groups.entries());
}

function ColorInput({
  label,
  value,
  placeholder,
  fallback,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  fallback: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm text-ui-text-muted mb-1">{label}</label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value || fallback}
          onChange={(event) => onChange(event.target.value)}
          className="w-10 h-9 rounded border border-ui-border bg-ui-bg cursor-pointer p-0.5"
        />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono focus:outline-none focus:border-ui-accent"
        />
      </div>
    </div>
  );
}
